import type { StripeEnvironment } from '../services/api';

// =============================================================================
// HubSpot Dashboard URLs
// =============================================================================

export function hubspotDealUrl(portalId: string, dealId: string): string {
  return `https://app.hubspot.com/contacts/${portalId}/deal/${dealId}`;
}

export function hubspotContactUrl(portalId: string, contactId: string): string {
  return `https://app.hubspot.com/contacts/${portalId}/contact/${contactId}`;
}

export function hubspotCompanyUrl(portalId: string, companyId: string): string {
  return `https://app.hubspot.com/contacts/${portalId}/company/${companyId}`;
}

// 0-7 is HubSpot's object type ID for products; they have no pretty URL path.
export function hubspotProductUrl(portalId: string, productId: string): string {
  return `https://app.hubspot.com/contacts/${portalId}/record/0-7/${productId}`;
}

// Universal Finrite HubSpot framework app id (same across all portals).
// Source of truth: backend/api/hubspot_oauth_router.py (_HUBSPOT_APP_ID).
const HUBSPOT_APP_ID = '37359808';

/** Deep-link to Finrite's installed app-cards settings page in HubSpot.
 *  Region-neutral host auto-redirects to the portal's region. */
export function hubspotAppCardsUrl(portalId: string): string {
  return (
    `https://app.hubspot.com/connected-apps/${encodeURIComponent(portalId)}` +
    `/installed/framework/${HUBSPOT_APP_ID}/settings/app-cards`
  );
}

// =============================================================================
// Stripe Dashboard URLs
// =============================================================================

function stripeBasePath(environment: StripeEnvironment, accountId?: string): string {
  switch (environment) {
    case 'live':
      return 'https://dashboard.stripe.com';
    case 'test':
      return 'https://dashboard.stripe.com/test';
    case 'sandbox':
      return `https://dashboard.stripe.com/b/${accountId}`;
    default:
      return 'https://dashboard.stripe.com/test';
  }
}

export function stripeSubscriptionUrl(
  environment: StripeEnvironment,
  subscriptionId: string,
  accountId?: string,
): string {
  return `${stripeBasePath(environment, accountId)}/subscriptions/${subscriptionId}`;
}

export function stripeCustomerUrl(
  environment: StripeEnvironment,
  customerId: string,
  accountId?: string,
): string {
  return `${stripeBasePath(environment, accountId)}/customers/${customerId}`;
}

export function stripeProductUrl(
  environment: StripeEnvironment,
  productId: string,
  accountId?: string,
): string {
  return `${stripeBasePath(environment, accountId)}/products/${productId}`;
}

export function stripePriceUrl(
  environment: StripeEnvironment,
  priceId: string,
  accountId?: string,
): string {
  return `${stripeBasePath(environment, accountId)}/prices/${priceId}`;
}

export function stripeMeterUrl(
  environment: StripeEnvironment,
  meterId: string,
  accountId?: string,
): string {
  return `${stripeBasePath(environment, accountId)}/billing/meters/${meterId}`;
}

export function stripeInvoiceUrl(
  environment: StripeEnvironment,
  invoiceId: string,
  accountId?: string,
): string {
  return `${stripeBasePath(environment, accountId)}/invoices/${invoiceId}`;
}
